import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import TextAreaField from '../../../../FormLegacy/FormFieldsV2/TextAreaField'
import ToggleField from '../../../../FormLegacy/FormFieldsV2/ToggleField'
import Stepper from '../../../../Stepper/Stepper'
import API from '../../../../../api/api'
import { useSnackbar } from '../../../../../helpers/hooks/useSnackbar'
import { createMaintenanceRequest } from '../../../../../reducers/maintenanceReducer'
import { ENTRY_NOTE_MAX_LENGTH } from '../../constants'
import ScheduledDateTimeFields from '../../../ScheduledDateTimeFields/ScheduledDateTimeFields'
import { useScheduledDateTime } from '../../../ScheduledDateTimeFields/useScheduledDateTime'

const INITIAL_FORM_STATE = {
  description: '',
  issue: null,
  location: null,
  pets: false,
  permissionToEnter: true,
  category: null,
  problem: null,
  locationType: null,
  commonArea: null,
  entryNote: '',
}

const getLocationTypes = (t) => [
  { value: 1, label: t('maintenance:form.requestDetails.unitOption') },
  { value: 4, label: t('maintenance:form.requestDetails.commonAreaOption') },
]

const REQUEST_TYPE_TO_LOCATION_TYPE_VALUE = {
  unit: 1,
  commonArea: 4,
}

const RealPageSettings = ({
  building,
  resident,
  assignee,
  requestType,
  onBack,
  onCancel,
  voicemailChatMessageSid,
  inspectionId,
  description,
  checklistRoomItemId,
  onSuccess,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const scheduledDateTime = useScheduledDateTime({ timezone: building.timezone })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const { displaySnackbar } = useSnackbar()
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const [realPageConfig, setRealPageConfig] = useState({
    categories: [],
    locations: [],
    commonAreas: [],
  })
  const [formErrors, setFormErrors] = useState({})
  const locationTypes = getLocationTypes(t)
  // Note: the request type picked on Request Details dictates the location type, so it's locked.
  const isLocationTypeLocked = REQUEST_TYPE_TO_LOCATION_TYPE_VALUE[requestType] !== undefined

  const handleFieldChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleEntryNoteChange = ({ target: { value, name } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))

    // Validate entry note length
    if (value.length > ENTRY_NOTE_MAX_LENGTH) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: t('maintenance:form.pmsSettings.entryNote.validation', {
          maxLength: ENTRY_NOTE_MAX_LENGTH,
        }),
      }))
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleToggleChange = ({ target: { name, checked } }) => {
    setFormState((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSubmit = async () => {
    if (!scheduledDateTime.validate()) return

    setIsSubmitting(true)
    const requestData = {
      buildingId: building.id,
      locationType: formState.locationType.value,
      issueLocation: formState.location.value,
      description: formState.description,
      enterPermission: formState.permissionToEnter,
      pets: formState.pets,
      entryNote: formState.entryNote,
      realPage: true,
      ...(formState.category && { category: formState.category.value }),
      ...(formState.problem && { problem: formState.problem.value }),
      ...(formState.issue && { issue: formState.issue.value }),
      ...(formState.locationType.value === 4 && { commonArea: formState.commonArea.value }),
      ...(assignee && { assignee: assignee.id }),
      ...(scheduledDateTime.timestamp && { scheduledDate: scheduledDateTime.timestamp }),
    }
    if (resident.clientId) {
      requestData.clientId = resident.clientId
    } else if (resident.unit) {
      requestData.unit = resident.unit
    }
    if (resident.sourceUnitId) {
      requestData.unitId = resident.sourceUnitId
    }
    if (voicemailChatMessageSid) {
      requestData.voicemailChatMessageSid = voicemailChatMessageSid
    }
    if (checklistRoomItemId) {
      requestData.checklistRoomItemId = checklistRoomItemId
    } else if (inspectionId) {
      requestData.inspectionId = inspectionId
    }

    try {
      await dispatch(createMaintenanceRequest(building.id, requestData))
      displaySnackbar(t('maintenance:form.pmsSettings.snackbar.created'))
      if (onSuccess) {
        onSuccess()
      }
      onCancel()
    } catch (error) {
      const message =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : t('maintenance:form.pmsSettings.snackbar.error')
      displaySnackbar(message, true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const areAllFieldsValid =
    !!formState.description &&
    !!formState.location &&
    !!formState.locationType &&
    (formState.locationType.value === 4 ? !!formState.commonArea : true) &&
    Object.keys(formErrors).length === 0

  useEffect(() => {
    setFormState((prev) => ({ ...prev, problem: null }))
  }, [formState.category])

  useEffect(() => {
    ;(async () => {
      const { data } = await API.getRealPageConfig(building.id)
      const {
        categories: { data: categoriesData = [] },
        commonAreas: { data: commonAreasData = [] },
        locations: { data: locationsData = [] },
        problems: { data: problemsData = [] },
        issues: { data: issuesData = [] },
      } = data

      const categoriesOptions = categoriesData.map((category) => {
        return {
          label: category.catName,
          value: category.catID,
          problems: problemsData
            .filter((problem) => problem.pcID === category.catID)
            .map((problem) => ({
              value: problem.piID,
              label: problem.piItemName,
              issues: issuesData
                .filter((issue) => issue.piID === problem.piID)
                .map((issue) => ({ label: issue.psDescription, value: issue.psID })),
            })),
        }
      })
      const commonAreasOptions = commonAreasData.map((commonArea) => ({
        value: commonArea.CommonAreaID,
        label: commonArea.CommonAreaName,
      }))
      const locationsOptions = locationsData.map((location) => ({
        value: location.codeName,
        label: location.codeDisplayName,
      }))
      setRealPageConfig({
        categories: categoriesOptions,
        commonAreas: commonAreasOptions,
        locations: locationsOptions,
      })
    })()
  }, [building, building.id])

  useEffect(() => {
    if (description) {
      setFormState((prev) => ({ ...prev, description }))
    }
  }, [description])

  useEffect(() => {
    const locationTypeValue = REQUEST_TYPE_TO_LOCATION_TYPE_VALUE[requestType]
    if (locationTypeValue === undefined) return

    setFormState((prev) => ({
      ...prev,
      locationType: locationTypes.find((type) => type.value === locationTypeValue) ?? null,
    }))
  }, [requestType])

  return (
    <div className="maintenance-request-modal">
      <Stepper
        steps={[
          t('maintenance:form.steps.requestDetails'),
          t('maintenance:form.steps.requestSettings'),
        ]}
        currentStep={1}
      />
      <div className="row">
        <ScheduledDateTimeFields {...scheduledDateTime.fieldProps} />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.requestDescription')}</div>
        <TextAreaField
          placeholder={t('maintenance:form.pmsSettings.descriptionPlaceholder')}
          name="description"
          value={formState.description}
          maxLength={250}
          onChange={handleFieldChange}
        />
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.realPage.category')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={realPageConfig.categories}
          name="category"
          value={formState.category}
          onChange={(e) => setFormState((prev) => ({ ...prev, category: e }))}
          placeholder={t('maintenance:form.realPage.categoryPlaceholder')}
        />
      </div>
      {formState.category && (
        <div className="row">
          <div className="row-header">{t('maintenance:form.realPage.problem')}</div>
          <Select
            className="select-container"
            classNamePrefix="select-primary"
            options={formState.category.problems}
            name="problem"
            value={formState.problem}
            onChange={(e) => setFormState((prev) => ({ ...prev, problem: e }))}
            placeholder={t('maintenance:form.realPage.problemPlaceholder')}
          />
        </div>
      )}
      {formState.category && formState.problem && (
        <div className="row">
          <div className="row-header">{t('maintenance:form.realPage.issue')}</div>
          <Select
            className="select-container"
            classNamePrefix="select-primary"
            options={formState.problem.issues}
            name="issue"
            value={formState.issue}
            onChange={(e) => setFormState((prev) => ({ ...prev, issue: e }))}
            placeholder={t('maintenance:form.realPage.issuePlaceholder')}
          />
        </div>
      )}
      <div className="row">
        <div className="row-header">{t('maintenance:form.realPage.locationType')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={locationTypes}
          name="locationType"
          value={formState.locationType}
          onChange={(e) => setFormState((prev) => ({ ...prev, locationType: e }))}
          placeholder={t('maintenance:form.realPage.locationTypePlaceholder')}
          isDisabled={isLocationTypeLocked}
        />
      </div>
      {formState.locationType && formState.locationType.value === 4 && (
        <div className="row">
          <div className="row-header">{t('maintenance:form.realPage.commonArea')}</div>
          <Select
            className="select-container"
            classNamePrefix="select-primary"
            options={realPageConfig.commonAreas}
            name="locationType"
            value={formState.commonArea}
            onChange={(e) => setFormState((prev) => ({ ...prev, commonArea: e }))}
            placeholder={t('maintenance:form.realPage.locationTypePlaceholder')}
          />
        </div>
      )}
      <div className="row">
        <div className="row-header">{t('maintenance:form.realPage.location')}</div>
        <Select
          className="select-container"
          classNamePrefix="select-primary"
          options={realPageConfig.locations}
          name="priority"
          value={formState.location}
          onChange={(e) => setFormState((prev) => ({ ...prev, location: e }))}
          placeholder={t('maintenance:form.realPage.locationPlaceholder')}
        />
      </div>
      <div className="row checkboxes">
        <div className="input-container">
          <ToggleField
            value={formState.pets}
            name="pets"
            label={t('maintenance:form.pmsSettings.pets')}
            labelOnRight
            onChange={handleToggleChange}
          />
        </div>
        <div className="input-container">
          <ToggleField
            name="permissionToEnter"
            label={t('maintenance:form.pmsSettings.permissionToEnter')}
            labelOnRight
            onChange={handleToggleChange}
            value={formState.permissionToEnter}
          />
        </div>
      </div>
      <div className="row">
        <div className="row-header">{t('maintenance:form.pmsSettings.entryNote.title')}</div>
        <TextAreaField
          name="entryNote"
          placeholder={t('maintenance:form.pmsSettings.entryNote.placeholder')}
          value={formState.entryNote}
          onChange={handleEntryNoteChange}
          isError={!!formErrors.entryNote}
        />
        {formErrors.entryNote && <span className={'label--error'}>{formErrors.entryNote}</span>}
      </div>
      <div className="modal-footer">
        <div className="buttons">
          <button className="button button-gray" onClick={onBack}>
            {t('maintenance:form.pmsSettings.back')}
          </button>
          <button className="button button-gray" onClick={onCancel}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary"
            disabled={isSubmitting || !areAllFieldsValid}
            onClick={handleSubmit}
          >
            {t('maintenance:form.pmsSettings.createRequest')}
          </button>
        </div>
      </div>
    </div>
  )
}

RealPageSettings.propTypes = {
  building: PropTypes.object,
  resident: PropTypes.object,
  assignee: PropTypes.object,
  requestType: PropTypes.string,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  voicemailChatMessageSid: PropTypes.bool,
  inspectionId: PropTypes.number,
  description: PropTypes.string,
  checklistRoomItemId: PropTypes.number,
  onSuccess: PropTypes.func,
}

export default RealPageSettings
